import React from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { merge, has, pickBy, isEmpty } from 'lodash';
import { List } from 'immutable';

import {
    ConfigsService as GeneralConfigService,
    LocaleService,
    ContentZonesService,
    UrlService,
    WindowProxy,
} from '@sm360/widget-commons-core-services';
import {
    AccountService,
    EVENTS,
    HistoryService,
    ReduxClient,
    AdobeService,
    AprService,
} from '@sm360/widget-commons-general';
import { PurchaseMethods, Vehicle } from '@sm360/widget-commons-vehicle';
import { EnvironmentConfigsService, ConfigsModel } from '@sm360/widget-commons-configs';

import Catalog from 'catalog/components/Catalog/Catalog.jsx';
import CatalogFilterModel from 'catalog/models/CatalogFilterModel.js';
import * as CatalogFilterService from 'catalog/services/CatalogFilterService.js';

import { loadMakes, setCatalogFilter } from 'catalog/actions/catalogActions.js';

import catalogFilterReducer from 'catalog/reducers/catalogFilterReducer.js';
import manufacturerReducer from 'catalog/reducers/manufacturerReducer.js';

import BodyStyleFilterModel from 'catalog/models/BodyStyleFilterModel.js';

import en from 'catalog/i18n/en.json';
import fr from 'catalog/i18n/fr.json';

const { TradeInVehicleActions } = Vehicle;
const { PurchaseMethodsActions, PurchaseMethodsModel } = PurchaseMethods;

class CatalogProvider extends React.Component {
    constructor(props) {
        super(props);

        merge(en, PurchaseMethods.Translations.en, Vehicle.Translation.en);
        merge(fr, PurchaseMethods.Translations.fr, Vehicle.Translation.fr);

        GeneralConfigService.setOrganizationId(props.organizationId);
        GeneralConfigService.setOrganizationUnitId(props.organizationUnitId);
        GeneralConfigService.setOrganizationUnitLocation(props.organizationUnitLocation);
        GeneralConfigService.setWebsiteId(props.websiteId);
        GeneralConfigService.setLocation(props.location);
        GeneralConfigService.setNoMakeListStr(props.noMakeListStr);
        GeneralConfigService.setModelListStr(props.modelListStr);

        const imageServerBaseUrl = (props && props.configs && props.configs.source && props.configs.source.image) || '';
        ContentZonesService.setImageServerBaseUrl(imageServerBaseUrl);
        ContentZonesService.addContentZones(props.contentZones);

        LocaleService.setLocale(props.locale);
        LocaleService.loadTranslations({ en, fr });

        this.storeRedux = ReduxClient.getOrCreate();
        ReduxClient.addReducers({
            catalogFilter: catalogFilterReducer,
            manufacturer: manufacturerReducer,
            tradeInVehicle: Vehicle.TradeInVehicleReducer,
            purchaseMethods: PurchaseMethods.PurchaseMethodsReducer,
        });

        EnvironmentConfigsService.init(props.configs);

        this.onTradeInCreated = this.onTradeInCreated.bind(this);
        this.checkTradeInVehicleStatus = this.checkTradeInVehicleStatus.bind(this);
        this.onUnload = this.onUnload.bind(this);

        this.state = {
            isLoading: true,
        };
    }

    async componentDidMount() {
        const $window = WindowProxy.get();
        if ($window) {
            $window.addEventListener(EVENTS.TRADEIN_CREATED, this.onTradeInCreated);
            $window.addEventListener('beforeunload', this.onUnload);
        }

        // For now we used to have Filters from XMS only for Honda and Acura
        // For all other brand filters are calculated from the vehicles bodystyle
        const isCustomBodyStyle =
            this.props.makes &&
            this.props.makes.length > 0 &&
            (this.props.makes.includes('HONDA') || this.props.makes.includes('ACURA')) &&
            true;
        const customBodyStyle = isCustomBodyStyle && (await CatalogFilterService.getCatalogFiltersGroups());

        const catalogModel = this.buildModel(customBodyStyle);

        const catalogFilter = CatalogFilterService.translateFilters(catalogModel, customBodyStyle);
        this.storeRedux.dispatch(setCatalogFilter(catalogFilter));
        this.storeRedux.dispatch(loadMakes(this.props.makes, this.props.locale)).finally(() => {
            this.setState({
                isLoading: false,
            });
        });
        this.checkTradeInVehicleStatus(true);
    }

    componentWillUnmount() {
        const $window = WindowProxy.get();

        if ($window) {
            $window.removeEventListener(EVENTS.TRADEIN_CREATED, this.onTradeInCreated);
            this.onUnload();
        }
    }

    onUnload() {
        HistoryService.add(WindowProxy.location().href);
    }

    onTradeInCreated() {
        this.checkTradeInVehicleStatus(true);
    }

    checkTradeInVehicleStatus(forceLoad) {
        const storeState = this.storeRedux.getState();
        if (forceLoad || isEmpty(storeState.tradeInVehicle) || !storeState.tradeInVehicle.id) {
            if (AccountService.isAuthenticated()) {
                this.storeRedux.dispatch(TradeInVehicleActions.loadTradeInVehicle(storeState.tradeInVehicle));
            } else {
                const defaultPurchaseMethods = new PurchaseMethodsModel();
                this.storeRedux.dispatch(PurchaseMethodsActions.setTradeIn(defaultPurchaseMethods.tradeIn));
                this.storeRedux.dispatch(PurchaseMethodsActions.setLien(defaultPurchaseMethods.lien));
            }
        }
    }

    buildModel(customBodyStyle) {
        const urlParams = UrlService.getUrlParameters();
        const bodyStyles =
            customBodyStyle &&
            customBodyStyle.length > 0 &&
            List(
                customBodyStyle?.map((filterType) => {
                    return new BodyStyleFilterModel({
                        type: filterType.name,
                        codes: filterType.codes,
                        value: filterType.name,
                        priority: filterType.priority,
                    });
                })
            );
        const conf = {
            preferredMake: has(urlParams, 'preferredMake') && urlParams.preferredMake,
            paymentFrequency: has(urlParams, 'paymentFrequency') && parseInt(urlParams.paymentFrequency, 10),
            purchaseMethodOrder: has(urlParams, 'purchaseMethodOrder') && parseInt(urlParams.purchaseMethodOrder, 10),
            bodyStylesFilterCodes:
                has(urlParams, 'bodyStylesFilterCodes') && urlParams.bodyStylesFilterCodes.split(','),
            bodyStylesFilterAll: !has(urlParams, 'bodyStylesFilterCodes'),
            customBodyStyle: bodyStyles && bodyStyles,
        };

        return new CatalogFilterModel(pickBy(conf, (value) => value));
    }

    render() {
        const orgUnitId = GeneralConfigService.getOrganizationUnitId();
        const location = GeneralConfigService?.getOrganizationUnitLocation();
        const displayCreditRate = AprService.displayApr(location, orgUnitId);
        const isCustomBodyStyle =
            this.props.makes &&
            this.props.makes.length > 0 &&
            (this.props.makes.includes('HONDA') || this.props.makes.includes('ACURA')) &&
            true;

        return (
            <Provider store={this.storeRedux}>
                <Catalog
                    buildAndQuoteUrl={this.props.buildAndQuoteUrl}
                    isLoading={this.state.isLoading}
                    hasAdobeTagging={AdobeService.isDigitalDataAvailable()}
                    displayCreditRate={displayCreditRate}
                    isCustomBodyStyle={isCustomBodyStyle}
                    noMakeListStr="land rover"
                    modelListStr="discovery"
                />
            </Provider>
        );
    }
}

CatalogProvider.defaultProps = {
    noMakeListStr: '',
    modelListStr: '',
};

CatalogProvider.propTypes = {
    makes: PropTypes.arrayOf(PropTypes.string).isRequired,
    location: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    websiteId: PropTypes.number.isRequired,
    buildAndQuoteUrl: PropTypes.string.isRequired,
    organizationUnitId: PropTypes.number.isRequired,
    organizationId: PropTypes.number.isRequired,
    organizationUnitLocation: PropTypes.string.isRequired,
    contentZones: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                active: PropTypes.bool,
                group: PropTypes.string,
                id: PropTypes.number,
                location: PropTypes.string,
                position: PropTypes.number,
                type: PropTypes.string,
                value: PropTypes.string,
            })
        )
    ).isRequired,
    configs: PropTypes.shape(ConfigsModel).isRequired,
    noMakeListStr: PropTypes.string,
    modelListStr: PropTypes.string,
};

CatalogProvider.displayName = 'CatalogProvider';

export default CatalogProvider;
